import { type PaletteKey, type PixelCanvas, sprite } from '../../../lib';
import { box, byRow, geometry, outlined, taper } from '../effect-guns.sprite';

/**
 * Abracadabra (the Magician set's gun, ADR-0160 amended): a magic wand made a pistol. A black lacquered body with a gold
 * trim line and a playing card inlaid on its side (the ace of hearts), a gold ring, and
 * from it the wand: glossy black, a glint running along it, its white tip at the muzzle (a white cap at the stock's end
 * too). On a shot (`fire`) the card flips to its red back and the tip flashes white, then gold, then settles.
 */

const LACQUER = ['k', '1', '2', '3'] as const;

const magic = (frame: number, heat = 0) =>
  outlined((c: PixelCanvas) => {
    // The body: black lacquer, lit on top, its back rounded off, a white cap on its end.
    box(c, 3, 14, 31, 26, [3, 1], byRow(14, 26, LACQUER, 1, 2), 'l');
    box(c, 3, 16, 5, 24, [2, 1], (x, y): PaletteKey => (y > 22 ? '4' : x === 3 ? '5' : 'w'), 'l');
    // A gold trim line along it.
    c.hline(7, 30, 17, 'Y');
    c.hline(8, 29, 16, 'y');
    // A playing card inlaid on its side, flipping over as it fires: the ace of hearts, then its red back.
    box(c, 7, 18, 12, 24, [1], (x, y): PaletteKey => {
      if (heat === 2) return x === 7 || y === 18 ? 'w' : 'r';
      if (x === 12 || y === 24) return '5';
      const heart = ((x === 8 || x === 10) && y === 20) || (x >= 8 && x <= 10 && y === 21) || (x === 9 && y === 22);
      return heart ? 'r' : 'w';
    });
    // The trigger under it.
    box(c, 26, 27, 29, 29, [0, 1], (_x, y): PaletteKey => (y === 27 ? 'Y' : 'U'), 'r');
    // The gold ring the wand comes out of.
    box(c, 31, 15, 34, 25, [1], (x, y): PaletteKey => (y < 17 ? 'y' : y > 23 ? 'U' : x === 31 ? 'y' : 'Y'));
    // The wand: glossy black, narrowing, a glint along its top (running along it, frame by frame).
    const glint = 37 + ((frame * 5) % 18);
    taper(c, 35, 55, 20, 3, 2, (top, bottom, x): PaletteKey => {
      if (top === 0) return Math.abs(x - glint) < 2 ? 'w' : '3';
      if (top === 1) return Math.abs(x - glint) < 1 ? '5' : '2';
      return bottom === 0 ? 'k' : '1';
    });
    // Its white tip, to the muzzle: flashing white, then gold, as it fires.
    taper(c, 56, 63, 20, 2, 1, (top, bottom): PaletteKey => {
      if (heat === 2) return 'w';
      if (heat === 1) return top === 0 ? 'w' : 'y';
      return top === 0 ? 'w' : bottom === 0 ? '4' : '5';
    });
  });

export default sprite({
  name: 'pollinator_magician',
  ...geometry,
  notes: `Abracadabra (ADR-0160 amended, the Magician set's gun): a magic wand made a pistol. A black lacquered body (lit
on top, a white cap on its rounded back end) with a gold trim line and a playing card inlaid on its side (a red heart on white), a gold ring, and the wand: glossy black, narrowing, a glint
running along its top over 4 frames, its white tip at the muzzle. Fire: the card flips to its red back and the tip flashes white, then gold, settling over 3 frames. Its flair: sparkles fizzing off the tip. No team keys. Rubric (4×):
S4 R4 P4 L4 C4 A4 F4.`,
  anims: {
    idle: { fps: 8, frames: [magic(0), magic(1), magic(2), magic(3)] },
    fire: { fps: 20, loop: false, frames: [magic(0, 2), magic(1, 2), magic(2, 1), magic(3)] },
  },
});
